/**
 * 订单列表数据处理 Hook
 */

import { useState, useEffect, useMemo } from 'react';
import dayjs from 'dayjs';
import { amazonOrderAPI } from '../../../services/api';
import { AmazonOrder, OrderTableRow, OrderItem } from './types';

export const useOrderList = () => {
    // State
    const [loading, setLoading] = useState(false);
    const [orders, setOrders] = useState<AmazonOrder[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);

    // Filters
    const [searchText, setSearchText] = useState('');
    const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);
    const [channelFilter, setChannelFilter] = useState<string | undefined>(undefined);
    const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(null);

    // Fetch Data
    const fetchOrders = async () => {
        setLoading(true);
        try {
            const params: any = {
                page,
                page_size: pageSize,
                ordering: '-purchase_date',
            };

            if (searchText) {
                params.search = searchText;
            }
            if (statusFilter) {
                params.order_status = statusFilter;
            }
            if (channelFilter) {
                params.fulfillment_channel = channelFilter;
            }
            if (dateRange) {
                params.purchase_date__gte = dateRange[0].format('YYYY-MM-DD');
                params.purchase_date__lte = dateRange[1].format('YYYY-MM-DD');
            }

            const response = await amazonOrderAPI.list(params);

            if (response.data.results) {
                setOrders(response.data.results);
                setTotal(response.data.count);
            } else {
                setOrders(response.data);
                setTotal(response.data.length);
            }
        } catch (error) {
            console.error('Fetch orders error:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchOrders();
    }, [page, pageSize, statusFilter, channelFilter, dateRange]);

    // Flatten data for table (One row per Item, formatted for RowSpan)
    const tableData = useMemo<OrderTableRow[]>(() => {
        const data: any[] = [];
        orders.forEach(order => {
            if (!order.items || order.items.length === 0) {
                data.push({ ...order, isHead: true, rowSpan: 1, isItem: false });
            } else {
                order.items.forEach((item: OrderItem, index: number) => {
                    data.push({
                        ...order,
                        ...item,
                        itemId: item.id,
                        isHead: index === 0,
                        rowSpan: index === 0 ? order.items.length : 0,
                        isItem: true,
                        itemIndex: index,
                        item_asin: item.asin,
                        item_sku: item.sku,
                        item_name: item.product_name,
                        item_price: item.item_price,
                        item_qty: item.quantity,
                        item_currency: item.currency,
                        item_subtotal: Number(item.item_price || 0) * item.quantity,
                    });
                });
            }
        });
        return data;
    }, [orders]);

    // Handlers
    const handleSearch = () => {
        setPage(1);
        fetchOrders();
    };

    const handleReset = () => {
        setSearchText('');
        setStatusFilter(undefined);
        setChannelFilter(undefined);
        setDateRange(null);
        setPage(1);
        fetchOrders();
    };

    return {
        // State
        loading,
        tableData,
        total,
        page,
        pageSize,
        // Filters
        searchText,
        statusFilter,
        channelFilter,
        dateRange,
        // Setters
        setPage,
        setPageSize,
        setSearchText,
        setStatusFilter,
        setChannelFilter,
        setDateRange,
        // Actions
        handleSearch,
        handleReset,
    };
};
